import { useCallback, useState } from 'react';
import { inversionApi } from '../lib/api';
import { fetchJobResult } from '../lib/jobs-api';
import { productToModel } from '../lib/inversion-parser';
import { buildDiffEntry, recordToEntry } from '../lib/inversion-results';
import { useJob } from './useJob';

/**
 * Time-lapse difference of two inversion model entries (B - A), Wave 3.3.
 *
 * Client-side models (the map panel's imports) are differenced in the browser with the
 * same rules as the server (lib/inversion-results.js). Entries that carry a stored
 * `backendId` and `projectId` go through POST /api/inversion/timelapse-diff: the server
 * answers 200 with the new model (under 5,000 cells) or 202 with a job id, which useJob
 * polls (`job.progress`, `job.message`) until the derived model can be loaded.
 * A georeference mismatch is reported as the server's 409 message.
 *
 *   onAddModel(entry) - add the derived model to the panel's list
 */
export default function useTimelapseDiff({ onAddModel }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [jobId, setJobId] = useState(null);

  const loadStored = useCallback(async (id) => {
    const res = await inversionApi.get(id);
    const entry = recordToEntry(res.data, productToModel);
    entry.projectId = res.data.project_id;
    onAddModel(entry);
    return entry;
  }, [onAddModel]);

  const { job, cancel } = useJob(jobId, {
    onSettled: async (j) => {
      try {
        if (j.status === 'succeeded') {
          const result = await fetchJobResult(j.id);
          if (result?.id) await loadStored(result.id);
        } else if (j.status === 'failed') {
          setError(j.error || j.message || 'The difference job failed');
        }
      } catch (err) {
        setError(err?.response?.data?.detail || err.message);
      } finally {
        setBusy(false);
        setJobId(null);
      }
    },
  });

  const compare = useCallback(async (entryA, entryB, name) => {
    setError(null);
    if (!entryA || !entryB || entryA.id === entryB.id) { setError('Choose two different models'); return null; }
    setBusy(true);
    try {
      if (entryA.backendId && entryB.backendId && entryA.projectId) {
        const res = await inversionApi.timelapseDiff({
          model_a_id: entryA.backendId, model_b_id: entryB.backendId, project_id: entryA.projectId, name,
        });
        if (res.status === 202) { setJobId(res.data.job_id); return null; } // busy stays on until the job settles
        const entry = await loadStored(res.data.id);
        setBusy(false);
        return entry;
      }
      const entry = buildDiffEntry(entryA, entryB, name);
      onAddModel(entry);
      setBusy(false);
      return entry;
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setBusy(false);
      return null;
    }
  }, [loadStored, onAddModel]);

  return { compare, busy, error, job, cancel };
}
